import { type SelectionMap } from '@/core/components/FilterSelector/types';
import { type SliderRange } from '@/core/components/CustomSlider/CustomSlider';
import { buildRangePayload, type RangePayload } from '@/core/components/RangePayload/RangePayload';
import { type AuthorOption } from '@/core/services/interfaces/AuthorInterfaces';

export const FOLLOWERS_MIN = 0;
export const FOLLOWERS_MAX = 5_000_000;

export const GENDER_NUMBERS = [1, 2, 3];

export type MyPagesFilterType = 'all' | 'myPages' | 'notMyPages';

export interface AuthorData {
  authorsSelection: SelectionMap;
  authorsMap: Record<string, AuthorOption>;
  interestsSelection: SelectionMap;
  profileTypeSelection: SelectionMap;
  followersRange: SliderRange;
  // Tracks whether the user explicitly interacted with the profile type selector. Since it
  // starts fully selected ("Todos") by default, we need this flag to tell an untouched default
  // apart from an explicit "select all" action when deciding whether to show the badge count.
  profileTypeTouched?: boolean;
  myPagesFilter?: MyPagesFilterType;
  manualAuthorKeys?: string[];
}

export const getAuthorKey = (author: AuthorOption): string =>
  `${author.channel.toLowerCase()}__${author.uid || author.username}`;

const buildAllSelectedMap = (items: string[]): SelectionMap =>
  Object.fromEntries(items.map((item) => [item, 'add']));

export const buildFollowersPayload = (range: SliderRange): RangePayload | undefined =>
  buildRangePayload(range, FOLLOWERS_MIN, FOLLOWERS_MAX);

export const buildAuthorDefaultData = (
  availableProfileTypes: string[],
): AuthorData => ({
  authorsSelection: {},
  authorsMap: {},
  interestsSelection: {},
  profileTypeSelection: buildAllSelectedMap(availableProfileTypes),
  followersRange: [FOLLOWERS_MIN, FOLLOWERS_MAX],
  profileTypeTouched: false,
  myPagesFilter: 'all',
  manualAuthorKeys: [],
});

export const formatAuthorsPayload = (
  authorsSelection: SelectionMap,
  authorsMap: Record<string, AuthorOption>,
): { value: string[]; users: Array<{ c: string; i: string; n: string; u: string }> } | undefined => {
  const entries = Object.entries(authorsSelection).filter(([, state]) => state !== null);
  if (entries.length === 0) {
    return undefined;
  }

  const value: string[] = [];
  const users: Array<{ c: string; i: string; n: string; u: string }> = [];
  const seenUids = new Set<string>();

  entries.forEach(([key, state]) => {
    const author = authorsMap[key];
    if (!author) return;

    const negationPrefix = state === 'remove' ? '-' : '';
    const normalizeLeadingDash = (value: string): string => value.replace(/^-+/, '');

    // Include all UIDs for the author
    const authorUids = author.uids && author.uids.length > 0 ? author.uids : [author.uid];
    authorUids.forEach(uid => {
      const normalizedUid = normalizeLeadingDash(uid);
      const prefixedUid = `${negationPrefix}${normalizedUid}`;
      if (!seenUids.has(prefixedUid)) {
        value.push(prefixedUid);
        seenUids.add(prefixedUid);
      }
    });

    // Add to users array (using primary UID only for user record)
    const primaryUid = normalizeLeadingDash(authorUids[0]);
    const normalizedName = normalizeLeadingDash(author.name);
    users.push({
      c: author.channel,
      i: `${negationPrefix}${primaryUid}`,
      n: `${negationPrefix}${normalizedName}`,
      u: author.username,
    });
  });

  return value.length > 0 ? { value, users } : undefined;
};

export const formatInterestsPayload = (interestsSelection: SelectionMap): string[] =>
  Object.entries(interestsSelection)
    .filter(([, state]) => state !== null)
    .map(([key, state]) => (state === 'remove' ? `-${key}` : key));

export const toAuthorPayload = (data: AuthorData) => {
  const authorsPayload = formatAuthorsPayload(data.authorsSelection, data.authorsMap);

  return {
    occupations: { value: formatInterestsPayload(data.interestsSelection) },
    profileTypeSelection: data.profileTypeSelection,
    selectedAuthorUids: authorsPayload?.value || [],
    authorsSelection: data.authorsSelection,
    authorsMap: data.authorsMap,
    followersRange: data.followersRange,
  };
};

export const applyMyPagesFilterToData = (
  currentData: AuthorData,
  nextFilter: MyPagesFilterType,
  connectedPages: AuthorOption[],
): AuthorData => {
  const nextSelection: SelectionMap = {};
  const nextMap: Record<string, AuthorOption> = {};

  if (nextFilter === 'all') {
    return { ...currentData, myPagesFilter: nextFilter, authorsSelection: nextSelection, authorsMap: nextMap };
  }

  const action = nextFilter === 'notMyPages' ? 'remove' : 'add';

  const manualKeys = currentData.manualAuthorKeys ?? [];
  manualKeys.forEach((key) => {
    const existingAction = currentData.authorsSelection[key];
    if (existingAction && currentData.authorsMap[key]) {
      nextSelection[key] = existingAction;
      nextMap[key] = currentData.authorsMap[key];
    }
  });

  connectedPages.forEach((page) => {
    const key = getAuthorKey(page);
    nextSelection[key] = action;
    nextMap[key] = page;
  });

  return { ...currentData, myPagesFilter: nextFilter, authorsSelection: nextSelection, authorsMap: nextMap };
};

export const buildAuthorEncodePayload = (
  data: AuthorData,
  profileTypeGenderMap: Record<string, number>,
  totalProfileTypes: number,
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {};

  const interests = formatInterestsPayload(data.interestsSelection);
  if (interests.length > 0) {
    payload.occupations = { value: interests };
  }

  const authorsPayload = formatAuthorsPayload(data.authorsSelection, data.authorsMap);
  if (authorsPayload) {
    payload.uids = authorsPayload;
  }

  const selectedGenders = Object.entries(data.profileTypeSelection)
    .filter(([, action]) => action === 'add')
    .map(([label]) => profileTypeGenderMap[label])
    .filter((v): v is number => v !== undefined);

  if (selectedGenders.length < totalProfileTypes) {
    payload.genders = { value: selectedGenders };
  }

  const followersPayload = buildFollowersPayload(data.followersRange);
  if (followersPayload) {
    payload.followers = followersPayload;
  }

  return payload;
};
